import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import {
  useCurrentShopAdmin,
  useUpdateShopAdmin,
} from "@/api/shops/use-shop-admin";
import {
  LegacyPortalReason,
  type LegacyPortalReasonMode,
} from "@/components/legacy-portal-reason/legacy-portal-reason";
import FormSwitch from "@babackapp/react-hook-forms-components/form-switch";
import useTranslation from "@babackapp/translations/use-translation";
import { Card, CardContent } from "@babackapp/ui/components/card";
import { Form, FormField } from "@babackapp/ui/components/form";
import { Skeleton } from "@babackapp/ui/components/skeleton";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";

const ShopAdminSettingsFormSchema = z.object({
  newPortal: z.boolean(),
  demo: z.boolean(),
  pushReasonExternalIdToShopifyNote: z.boolean(),
});

type ShopAdminSettingsFormValues = z.infer<typeof ShopAdminSettingsFormSchema>;

/** Each switch reads `<translationKey>.title` and `<translationKey>.description`. */
const SWITCHES: {
  name: keyof ShopAdminSettingsFormValues;
  translationKey: string;
}[] = [
  {
    name: "newPortal",
    translationKey: "settings.shop.admin.returnWidget.enabled",
  },
  { name: "demo", translationKey: "settings.shop.admin.returnWidget.demo" },
  {
    name: "pushReasonExternalIdToShopifyNote",
    translationKey: "settings.shop.admin.pushReasonExternalIdToShopifyNote",
  },
];

function ShopAdminSettingsForm({
  legacyPortalReason,
  ...defaultValues
}: ShopAdminSettingsFormValues & { legacyPortalReason: string | null }) {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const { mutateAsync: updateShopAdmin, isPending } = useUpdateShopAdmin(
    shop.id,
  );
  const [reasonMode, setReasonMode] = useState<LegacyPortalReasonMode | null>(
    null,
  );

  const form = useForm<ShopAdminSettingsFormValues>({
    mode: "onChange",
    defaultValues,
  });

  const values = useWatch({ control: form.control });

  const onSubmit = form.handleSubmit((next: ShopAdminSettingsFormValues) =>
    updateShopAdmin({
      returnWidget: { demo: next.demo, enabled: next.newPortal },
      pushReasonExternalIdToShopifyNote: next.pushReasonExternalIdToShopifyNote,
    }).then(() => form.reset(next)),
  );

  useEffect(() => {
    if (form.formState.isDirty) onSubmit();
  }, [values, form.formState.isDirty]);

  // Switching the V3 portal off is saved from the reason dialog, not by the
  // auto-submit: the field only moves once the reason is accepted.
  const saveReason = (reason: string, mode: LegacyPortalReasonMode) =>
    updateShopAdmin({
      returnWidget:
        mode === "switch-off"
          ? { enabled: false, legacyPortalReason: reason }
          : { legacyPortalReason: reason },
    }).then(() => {
      if (mode === "switch-off") {
        form.reset({ ...form.getValues(), newPortal: false });
      }
      setReasonMode(null);
    });

  return (
    <Form {...form}>
      <form>
        {SWITCHES.map(({ name, translationKey }) => (
          <FormField
            key={name}
            control={form.control}
            name={name}
            render={({ field }) =>
              name === "newPortal" ? (
                <FormSwitch
                  label={
                    <span className="inline-flex items-center gap-2">
                      {t(`${translationKey}.title`)}
                      <LegacyPortalReason
                        isEnabled={field.value}
                        reason={legacyPortalReason}
                        mode={reasonMode}
                        onModeChange={setReasonMode}
                        isPending={isPending}
                        onSubmit={saveReason}
                      />
                    </span>
                  }
                  description={t(`${translationKey}.description`)}
                  {...field}
                  onChange={(next) =>
                    next ? field.onChange(true) : setReasonMode("switch-off")
                  }
                />
              ) : (
                <FormSwitch
                  label={t(`${translationKey}.title`)}
                  description={t(`${translationKey}.description`)}
                  {...field}
                />
              )
            }
          />
        ))}
      </form>
    </Form>
  );
}

/** Baback-internal settings. Render only behind `useViewerIsSuperAdmin()`. */
export default function ShopAdminSettingsCard() {
  const { data, isPending } = useCurrentShopAdmin();

  return (
    <Card className="w-full">
      <CardContent className="py-8 px-8">
        {isPending ? (
          <Skeleton className="h-10 w-full" />
        ) : (
          <ShopAdminSettingsForm
            newPortal={data?.returnWidget?.enabled ?? false}
            legacyPortalReason={data?.returnWidget?.legacyPortalReason ?? null}
            demo={data?.returnWidget?.demo ?? false}
            pushReasonExternalIdToShopifyNote={
              data?.pushReasonExternalIdToShopifyNote ?? false
            }
          />
        )}
      </CardContent>
    </Card>
  );
}
